import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { parseApiError } from '@saas-kit/lib';
import {
  Button,
  Card,
  CardContent,
  Checkbox,
  ErrorBanner,
  Spinner,
  toast,
} from '@saas-kit/ui';

import {
  listPreferences,
  NOTIFICATION_KINDS,
  updatePreferences,
  type NotificationPreference,
} from './api';

export function PreferencesTab(): JSX.Element {
  const { t } = useTranslation('notifications');
  const queryClient = useQueryClient();
  const [emailEnabled, setEmailEnabled] = useState<Record<string, boolean>>({});

  const query = useQuery({
    queryKey: ['tenant-notification-preferences'],
    queryFn: listPreferences,
  });

  // The backend only persists overrides; an absent (kind, email) row means the
  // channel defaults to enabled. Seed the grid from that contract.
  useEffect(() => {
    if (!query.data) return;
    const next: Record<string, boolean> = {};
    for (const kind of NOTIFICATION_KINDS) {
      const pref = query.data.find(
        (p) => p.kind === kind && p.channel === 'email',
      );
      next[kind] = pref ? pref.enabled : true;
    }
    setEmailEnabled(next);
  }, [query.data]);

  const saveMutation = useMutation({
    mutationFn: () => {
      const items: NotificationPreference[] = NOTIFICATION_KINDS.map(
        (kind) => ({
          kind,
          channel: 'email',
          enabled: emailEnabled[kind] ?? true,
        }),
      );
      return updatePreferences(items);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['tenant-notification-preferences'],
      });
      toast.success(t('preferences.toasts.saved'));
    },
    onError: (e) => toast.error(parseApiError(e).message),
  });

  if (query.isLoading) {
    return (
      <div className="flex justify-center py-10">
        <Spinner />
      </div>
    );
  }
  if (query.isError) {
    return <ErrorBanner>{t('errors.load_failed')}</ErrorBanner>;
  }

  return (
    <Card>
      <CardContent className="p-0">
        <div className="border-b border-border px-5 py-3">
          <p className="text-sm font-medium text-foreground">
            {t('preferences.section_title')}
          </p>
          <p className="text-xs text-muted-foreground">
            {t('preferences.section_body')}
          </p>
        </div>
        <ul className="divide-y divide-border">
          {NOTIFICATION_KINDS.map((kind) => (
            <li
              key={kind}
              className="flex items-center justify-between gap-4 px-5 py-3"
            >
              <p className="min-w-0 truncate text-sm font-medium text-foreground">
                {t(`kinds.${kind}`, { defaultValue: kind })}
              </p>
              <label className="flex shrink-0 items-center gap-2 text-sm text-muted-foreground">
                <Checkbox
                  checked={emailEnabled[kind] ?? true}
                  onChange={() =>
                    setEmailEnabled((cur) => ({
                      ...cur,
                      [kind]: !(cur[kind] ?? true),
                    }))
                  }
                  data-testid={`pref-email-${kind}`}
                />
                {t('preferences.email_label')}
              </label>
            </li>
          ))}
        </ul>
        <div className="flex items-center justify-between gap-3 border-t border-border px-5 py-3">
          <p className="text-xs text-muted-foreground">
            {t('preferences.in_app_note')}
          </p>
          <Button
            onClick={() => saveMutation.mutate()}
            disabled={saveMutation.isPending}
          >
            {saveMutation.isPending
              ? t('preferences.saving')
              : t('preferences.save')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
